import { type Role } from '@/modules/receipts/types';

export enum ManualShippingStatus {
  AwaitingShipping = 'awaitingShipping',
  Shipped = 'shipped',
  Unknown = 'unknown',
}

export enum DepopShippingStatus {
  AwaitingShipping = 'awaitingShipping',
  InTransit = 'inTransit',
  Delivered = 'delivered',
  Failed = 'failed',
  Returned = 'returned',
  Unknown = 'unknown',
  ShippingLabelExpired = 'shippingLabelExpired',
}

type Tracking = {
  reference: string;
  url: string;
};

// ParcelBase is the core structure that
// all parcels implement, it should be
// be used to create other types of parcel
// in type guards where parcel type is unknown
export type ParcelBase<S, T> = {
  parcelId: string;
  parcelStatus: S;
  shippingProvider: string;
  purchaseLineItems?: number[];
  shippingDeadline?: string;
} & T;

export type CourierCollection = {
  address: {
    name: string;
    address: string;
    address2?: string;
    city: string;
    state?: string;
    postcode?: string;
    country: string;
  };
  collectionDate?: string;
};

export declare namespace ListParcel {
  type ManualShipping = ParcelBase<
    ManualShippingStatus,
    {
      shippedAt?: string;
      shippingTrackingNumber?: string;
      shippingCarrierName?: string;
    }
  >;
  type DepopShipping = ParcelBase<
    DepopShippingStatus,
    {
      providerName: string;
      tracking?: Tracking;
      shipmentTimestamp?: string;
      deliveryTimestamp?: string;
    }
  >;
  type AnyParcel = DepopShipping | ManualShipping;
}

export function isListDepopShipping(
  parcel: ListParcel.DepopShipping | ListParcel.ManualShipping
): parcel is ListParcel.DepopShipping {
  if (!parcel.shippingProvider) {
    return false;
  }
  return parcel.shippingProvider.toUpperCase() !== 'MANUAL';
}

export function isListManualShipping(
  parcel: ListParcel.DepopShipping | ListParcel.ManualShipping
): parcel is ListParcel.ManualShipping {
  return !isListDepopShipping(parcel);
}

export type DepopShippingProviderInfo = {
  providerId: string;
  providerName: string;
};

export declare namespace Parcel {
  // DepopShipping is the common structure used
  // across Depop based shipping methods, it
  // exists to reduce duplication of these properties
  type DepopShipping<T = undefined> = ListParcel.DepopShipping & {
    payer: Role;
    depopShippingProvider?: DepopShippingProviderInfo & T;
  };

  type ManualShipping = ListParcel.ManualShipping & {
    payer: Role;
  };

  type DepopShippingDropOff = DepopShipping<{
    dropOffLocatorUrl: string;
  }>;
  type DepopShippingCourier = DepopShipping<{
    courierCollection: CourierCollection;
  }>;

  type AnyParcel =
    | DepopShipping
    | DepopShippingCourier
    | DepopShippingDropOff
    | ManualShipping;
}

export function isManualShipping(
  parcel: Parcel.AnyParcel
): parcel is Parcel.ManualShipping {
  if (
    parcel &&
    !Boolean('depopShippingProvider' in parcel) &&
    !Boolean('tracking' in parcel)
  ) {
    return true;
  }

  return false;
}
